import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, subtask, type Subtask } from "@listto/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";

export async function createSubtaskMutation(
  db: PowerSyncDrizzleDB,
  todoId: string,
  text: string,
  currentSubtasks: readonly Subtask[],
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  let maxOrder = 0;
  for (const s of currentSubtasks) {
    maxOrder = Math.max(maxOrder, s.order ?? 0);
  }
  await db.insert(subtask).values({
    id,
    todo_id: todoId,
    text,
    completed: 0,
    order: maxOrder + 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateSubtaskMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{ text: string; completed: number; order: number }>,
): Promise<void> {
  await db
    .update(subtask)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(subtask.id, id));
}

export async function toggleSubtaskMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  currentCompleted: boolean,
): Promise<void> {
  await db
    .update(subtask)
    .set({ completed: currentCompleted ? 0 : 1, updated_at: nowISO() })
    .where(eq(subtask.id, id));
}

export async function deleteSubtaskMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(subtask).where(eq(subtask.id, id));
}

export async function moveSubtaskMutation(
  db: PowerSyncDrizzleDB,
  subtasks: readonly Subtask[],
  id: string,
  direction: "up" | "down",
): Promise<void> {
  const index = subtasks.findIndex((s) => s.id === id);
  if (index === -1) {
    return;
  }
  if (direction === "up" && index === 0) {
    return;
  }
  if (direction === "down" && index === subtasks.length - 1) {
    return;
  }

  const otherIndex = direction === "up" ? index - 1 : index + 1;
  const me = subtasks[index];
  const other = subtasks[otherIndex];
  if (!me || !other) {
    return;
  }

  const now = nowISO();

  await db.transaction(async (trx) => {
    await trx
      .update(subtask)
      .set({ order: other.order, updated_at: now })
      .where(eq(subtask.id, me.id));
    await trx
      .update(subtask)
      .set({ order: me.order, updated_at: now })
      .where(eq(subtask.id, other.id));
  });
}
